import { scaleBand, scaleLinear } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { Bar } from '@visx/shape'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import './chart.css'

export interface TargetActualChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
}

export function TargetActualChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
}: TargetActualChartProps) {
  const margin = { top: 20, right: 20, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  const allY = data.series.flatMap((s) => s.points.map((p) => p.y))
  const globalTarget = typeof data.target === 'number'
    ? data.target
    : (spec.target?.kind === 'number' ? spec.target.value : null)

  const candidateMax = [...allY, ...(globalTarget !== null ? [globalTarget] : [])]
  const maxY = Math.max(...candidateMax, 1)

  const xScale = scaleBand<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.35,
  })

  const yScale = scaleLinear<number>({
    domain: [0, maxY],
    range: [yMax, 0],
    nice: true,
  })

  const barWidth = xScale.bandwidth() || 20
  const seriesStyle = getSeriesStyle(0, spec.style?.seriesColor?.actual)

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Target vs Actual Chart'}
    >
      <Group left={margin.left} top={margin.top}>
        {/* Actual bars */}
        {data.series[0]?.points.map((point, idx) => {
          const xVal = String(point.x)
          const barX = xScale(xVal) || 0
          const barY = yScale(point.y)
          const barHeight = Math.max(0, yMax - barY)

          return (
            <Group key={idx}>
              <Bar
                x={barX}
                y={barY}
                width={barWidth}
                height={barHeight}
                fill={seriesStyle.fill}
                rx={3}
              />
              {/* Point or global target marker line */}
              {globalTarget !== null && (
                <line
                  x1={barX - 4}
                  y1={yScale(globalTarget)}
                  x2={barX + barWidth + 4}
                  y2={yScale(globalTarget)}
                  stroke={CHART_AXIS_COLOR}
                  strokeWidth={3}
                  strokeDasharray="4 2"
                  data-testid="chart-target-marker"
                />
              )}
            </Group>
          )
        })}

        {/* Global target reference line if no points */}
        {data.series[0]?.points.length === 0 && globalTarget !== null && (
          <line
            x1={0}
            y1={yScale(globalTarget)}
            x2={xMax}
            y2={yScale(globalTarget)}
            stroke={CHART_AXIS_COLOR}
            strokeWidth={2}
            strokeDasharray="4 4"
          />
        )}

        {/* Axes */}
        <AxisBottom
          scale={xScale}
          top={yMax}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'middle',
          }}
        />
        <AxisLeft
          scale={yScale}
          stroke={CHART_AXIS_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'end',
            dx: -4,
            dy: 3,
          }}
        />
      </Group>
    </svg>
  )
}
